import React from 'react'
import styled from 'styled-components'
import PreviewCard from './PreviewCard'
import { getHudColorHex } from '@/utils/constants'

// --- Styled Components for HUD ---

const Hud_TelemetryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
`

const Hud_EdpiBox = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
`

const Hud_EdpiValue = styled.div`
    font-size: 30px;
    font-weight: 900;
    font-family: var(--font-chakra);
    color: ${props => props.$color || '#6366f1'};
    line-height: 1;
    text-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
`

const Hud_EdpiLabel = styled.div`
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 9px;
    font-weight: 700;
`

const Hud_RadarCircle = styled.div`
    position: relative;
    right: 0px;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    border: 1px solid rgba(99, 102, 241, 0.2);
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
    }
`

const Hud_RadarCrosshair = styled.div`
    width: ${props => props.$vertical ? '1px' : '100%'};
    height: ${props => props.$vertical ? '100%' : '1px'};
    background: rgba(255, 255, 255, 0.05);
    position: absolute;
`

const Hud_RadarPing = styled.div`
    width: 4px;
    height: 4px;
    background: #6366f1;
    border-radius: 50%;
    position: absolute;
    box-shadow: 0 0 8px #6366f1;
    transform: ${props => `translateY(${props.$centered ? '0px' : '10px'}) scale(${props.$scale * 2})`};
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`

const Hud_MouseStatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 15px;
    margin-top: 10px;
    width: 100%;
`

const Hud_MiniStat = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${props => props.$alignStart ? 'flex-start' : 'initial'};
`

const Hud_MiniStatVal = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    font-family: var(--font-roboto);
`

const Hud_MiniStatLab = styled.div`
    font-size: 8px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.3);
    font-weight: 600;
    letter-spacing: 0.8px;
    margin-top: 2px;
`

const Hud_ColorSquare = styled.div`
    width: 16px;
    height: 16px;
    background: ${props => props.$color || '#fff'};
    border-radius: 4px;
    border: 1px solid rgba(255,255,255,0.2);
    box-shadow: 0 2px 8px ${props => props.$color || '#fff'}22;
    margin-top: 4px;
    transition: all 0.3s ease;
`

const Hud_RotateIcon = styled.div`
    opacity: ${props => props.$active ? 1 : 0.3};
    color: ${props => props.$active ? '#6366f1' : '#fff'};
    height: 22px;
    display: flex;
    align-items: center;
`

// --- Main Component ---

export default function HudPreview({
    data,
    isActive,
    isDisabled, // usually !configName
    onToggle,
    onClick,
    t,
    ...props
}) {
    const h = data || {}
    const hudColor = getHudColorHex(h.cl_hud_color)
    const txt = (v) => !isDisabled ? v : '-'

    return (
        <PreviewCard
            isActive={isActive}
            isDisabled={isDisabled}
            onClick={onClick}
            onToggle={onToggle}
            label="HUD & RADAR"
            {...props}
            style={{
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(50, 20, 30, 0.1) 100%)',
                ...props.style
            }}
        >
            <Hud_TelemetryHeader>
                <Hud_EdpiBox>
                    <Hud_EdpiValue $color={hudColor}>{txt(h.cl_radar_scale)}</Hud_EdpiValue>
                    <Hud_EdpiLabel>{t('RADAR_SCALE')}</Hud_EdpiLabel>
                </Hud_EdpiBox>
                <Hud_RadarCircle>
                    <Hud_RadarPing
                        $centered={Number(h.cl_radar_always_centered) === 1}
                        $scale={h.cl_radar_scale || 1}
                    />
                    <Hud_RadarCrosshair $vertical />
                    <Hud_RadarCrosshair />
                </Hud_RadarCircle>

            </Hud_TelemetryHeader>

            <Hud_MouseStatsGrid>
                {/* TL: Color */}
                <Hud_MiniStat $alignStart>
                    <Hud_ColorSquare $color={hudColor} />
                    <Hud_MiniStatLab style={{ marginTop: '4px' }}>{t('COLOR')}</Hud_MiniStatLab>
                </Hud_MiniStat>

                {/* TR: Rotate */}
                <Hud_MiniStat>
                    <Hud_RotateIcon $active={h.cl_radar_rotate}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                        </svg>
                    </Hud_RotateIcon>
                    <Hud_MiniStatLab>{!isDisabled ? (h.cl_radar_rotate ? t('ROTATE') : t('FIXED')) : '-'}</Hud_MiniStatLab>
                </Hud_MiniStat>

                {/* BL: Radar Size */}
                <Hud_MiniStat $alignStart>
                    <Hud_MiniStatVal>{txt(h.cl_hud_radar_scale)}</Hud_MiniStatVal>
                    <Hud_MiniStatLab>{t('RADAR_SIZE')}</Hud_MiniStatLab>
                </Hud_MiniStat>

                {/* BR: HUD Scale */}
                <Hud_MiniStat >
                    <Hud_MiniStatVal >{txt(h.hud_scaling)}</Hud_MiniStatVal>
                    <Hud_MiniStatLab>{t('HUD_SCALE')}</Hud_MiniStatLab>
                </Hud_MiniStat>
            </Hud_MouseStatsGrid>
        </PreviewCard>
    )
}
